// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Nextmine: tjek en rabatkode (NOTIFY10) for den aktuelle bruger, før den
// bruges i checkout. Returnerer { valid, reason?, percent? }.
// Selve håndhævelsen sker altid igen server-side ved betalingstransitionen.
export const discountStatus = body => {
  return post('/api/discount-status', body);
};

// Nextmine: registrér et trackingnummer hos AfterShip (via vores server), så
// pakken overvåges automatisk. Kaldes når sælger markerer afsendt.
// Plain JSON (server bruger bodyParser.json), derfor direkte fetch.
export const registerTracking = ({ transactionId, trackingNumber, carrierSlug }) => {
  return window
    .fetch(`${apiBaseUrl()}/api/register-tracking`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId, trackingNumber, carrierSlug }),
    })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          throw Object.assign(new Error('register-tracking failed'), data);
        });
      }
      return res.json();
    });
};

// Nextmine: offentlig Q&A på varesiden. Plain JSON (server bruger
// bodyParser.json), derfor direkte fetch med credentials (bruger-cookie).
const postJson = (path, body) => {
  return window
    .fetch(`${apiBaseUrl()}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    .then(res => {
      return res.json().then(data => {
        if (res.status >= 400) {
          throw Object.assign(new Error(data?.message || data?.error || 'Request failed'), {
            status: res.status,
            ...data,
          });
        }
        return data;
      });
    });
};

// Stil et offentligt spørgsmål til en vare. Returnerer { questions }.
export const postListingQuestion = ({ listingId, question }) => {
  return postJson('/api/listing-question', { listingId, question });
};

// Sælgers svar på et spørgsmål. Returnerer { questions }.
export const postListingAnswer = ({ listingId, questionId, answer }) => {
  return postJson('/api/listing-answer', { listingId, questionId, answer });
};

// Nextmine: "nudge" på efterlysninger. Options = brugerens egne live varer.
export const fetchIsoNudgeOptions = () => {
  return postJson('/api/iso-nudge-options', {});
};
export const sendIsoNudge = ({ requestId, listingId }) => {
  return postJson('/api/iso-nudge', { requestId, listingId });
};

// Nextmine: video paa en annonce. Serveren opretter videoen hos Bunny Stream
// og udsteder en kortlivet signatur; selve filen sendes derfra direkte af
// browseren, saa den aldrig gaar gennem vores egen server.
export const createVideoUploadUrl = () => {
  return postJson('/api/video-upload-url', {});
};

// Er video slaaet til paa denne markedsplads? Bruges til at skjule feltet
// helt frem for at vise noget, der fejler naar man klikker.
export const fetchVideoEnabled = () => {
  return window
    .fetch(`${apiBaseUrl()}/api/video-enabled`, { credentials: 'include' })
    .then(res => res.json())
    .then(data => !!data?.enabled)
    .catch(() => false);
};

// Videovaerten skal naa at kode videoen, foer den kan afspilles.
export const fetchVideoStatus = uid => {
  return window
    .fetch(`${apiBaseUrl()}/api/video-status?uid=${encodeURIComponent(uid)}`, {
      credentials: 'include',
    })
    .then(res =>
      res.json().then(data => {
        if (res.status >= 400) {
          throw Object.assign(new Error(data?.error || 'Request failed'), { status: res.status });
        }
        return data;
      })
    );
};

// Nextmine: godkend et netop oprettet samlingsstykke, så det bliver synligt
// med det samme i stedet for at ligge og vente på operatør-godkendelse.
export const approveCollectionPiece = listingId => {
  return postJson('/api/approve-collection-piece', { listingId });
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};
